/**
 * An attachment as it appears inside a message.
 *
 * Images and video render in place; everything else is a file card. Download
 * URLs are short-lived and are never part of the Attachment shape, so they are
 * fetched on demand — and only once the attachment has actually been scrolled
 * to, because a channel with a year of screenshots in it would otherwise ask
 * the server to mint a signed URL for every one of them on load.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactElement, RefObject } from 'react'

import { api, isApiError } from '@/api/client'
import type { Attachment, DownloadLink } from '@/api/types'
import { Modal, Spinner } from '@/components/ui'
import { formatBytes, formatFileKind } from '@/lib/format'
import styles from './AttachmentView.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .join(' ')
}

/**
 * What may be rendered inline, by type rather than by family.
 *
 * The mime type on an attachment is whatever the uploading client declared, so
 * it is a hint and not a fact — which is exactly why this is an allowlist. A
 * `image/*` prefix test would let `image/svg+xml` through, and an SVG is a
 * document that can carry script and can fetch remote resources; rendering one
 * because somebody labelled it an image is how a chat client turns a file
 * upload into a way of reaching every reader's browser.
 */
const INLINE_IMAGE_TYPES: ReadonlySet<string> = new Set([
  'image/apng',
  'image/avif',
  'image/bmp',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/webp',
])

const INLINE_VIDEO_TYPES: ReadonlySet<string> = new Set([
  'video/mp4',
  'video/ogg',
  'video/quicktime',
  'video/webm',
])

type MediaKind = 'image' | 'video' | 'file'

function normaliseMime(mimeType: string): string {
  return (mimeType.split(';')[0] ?? '').trim().toLowerCase()
}

function mediaKind(mimeType: string): MediaKind {
  const type = normaliseMime(mimeType)
  if (INLINE_IMAGE_TYPES.has(type)) return 'image'
  if (INLINE_VIDEO_TYPES.has(type)) return 'video'
  return 'file'
}

/**
 * The endpoint itself, not a signed URL: a browser navigation carries the
 * session cookie, the server answers 302 to a fresh short-lived location, and
 * the link therefore never goes stale in the page. That also keeps
 * middle-click, "save link as" and keyboard activation working, which a
 * JavaScript click handler would not.
 */
function downloadHref(attachmentId: string): string {
  return `${api.getBasePath()}/attachments/${encodeURIComponent(attachmentId)}/download`
}

// ── Lazy visibility ─────────────────────────────────────────────────────────

/**
 * True once the element has come near the viewport, and true forever after:
 * media that has been loaded should not be torn down again as it scrolls past.
 */
function useInView(ref: RefObject<Element>): boolean {
  const [inView, setInView] = useState(false)

  useEffect(() => {
    if (inView) return undefined

    const node = ref.current
    if (node === null) return undefined

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          setInView(true)
          observer.disconnect()
          return
        }
      },
      // Start a little before the attachment arrives, so scrolling at a normal
      // speed does not show an empty box first.
      { rootMargin: '300px 0px' },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [inView, ref])

  return inView
}

// ── Download link ───────────────────────────────────────────────────────────

interface DownloadState {
  url: string | null
  loading: boolean
  error: string | null
}

const IDLE: DownloadState = { url: null, loading: false, error: null }

function useDownloadLink(
  attachmentId: string,
  preview: boolean,
  enabled: boolean,
): DownloadState & { reload: () => void } {
  const [state, setState] = useState<DownloadState>(IDLE)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!enabled) {
      setState(IDLE)
      return undefined
    }

    const controller = new AbortController()
    setState({ url: null, loading: true, error: null })

    api
      .get<DownloadLink>(
        `/attachments/${encodeURIComponent(attachmentId)}/download`,
        preview ? { disposition: 'inline', variant: 'preview' } : { disposition: 'inline' },
        controller.signal,
      )
      .then((link) => {
        if (controller.signal.aborted) return
        setState({ url: link.url, loading: false, error: null })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setState({
          url: null,
          loading: false,
          error: isApiError(error) ? error.message : 'This file could not be loaded.',
        })
      })

    return () => controller.abort()
  }, [attachmentId, attempt, enabled, preview])

  const reload = useCallback((): void => setAttempt((value) => value + 1), [])

  return { ...state, reload }
}

// ── Icons ───────────────────────────────────────────────────────────────────

function DownloadIcon(): ReactElement {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <path d="M10 3.5v9m0 0 3.25-3.25M10 12.5 6.75 9.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 14.5v1a1.5 1.5 0 0 0 1.5 1.5h9a1.5 1.5 0 0 0 1.5-1.5v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function ShieldIcon(): ReactElement {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <path d="M10 2.75 4.75 4.9v4.35c0 3.2 2.1 6.1 5.25 7.1 3.15-1 5.25-3.9 5.25-7.1V4.9z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M7.75 9.75 10 12l2.25-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function DocumentIcon({ mimeType }: { mimeType: string }): ReactElement {
  const family = normaliseMime(mimeType).split('/')[0] ?? ''

  if (family === 'audio') {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
        <path d="M13 3.5v9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <path d="M13 5.5 17 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="10.5" cy="13.5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M11.5 2.75H6.5a1.75 1.75 0 0 0-1.75 1.75v11a1.75 1.75 0 0 0 1.75 1.75h7a1.75 1.75 0 0 0 1.75-1.75V6.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M11.5 2.75V6.5h3.75" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  )
}

// ── Non-downloadable states ─────────────────────────────────────────────────

interface NoticeProps {
  tone: 'quiet' | 'danger'
  icon: ReactElement
  title: string
  detail: string
}

/*
 * Deliberately not a live region. A blocked or deleted file is a fact about
 * history, not something that just happened, and a screen reader meeting fifty
 * of them while scrolling should read them in place rather than announce them.
 */
function Notice({ tone, icon, title, detail }: NoticeProps): ReactElement {
  return (
    <div className={cx(styles.notice, tone === 'danger' && styles.noticeDanger)}>
      <span className={styles.noticeIcon} aria-hidden="true">
        {icon}
      </span>
      <span className={styles.noticeBody}>
        <span className={styles.noticeTitle}>{title}</span>
        <span className={styles.noticeDetail}>{detail}</span>
      </span>
    </div>
  )
}

// ── The attachment ──────────────────────────────────────────────────────────

export interface AttachmentViewProps {
  attachment: Attachment
  className?: string
}

export function AttachmentView({ attachment, className }: AttachmentViewProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null)
  const inView = useInView(containerRef)

  const [previewFailed, setPreviewFailed] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [mediaBroken, setMediaBroken] = useState(false)
  // One retry only. A URL that has just expired is worth re-minting; a codec
  // the browser cannot decode would otherwise loop forever.
  const retried = useRef(false)

  // `ready` alone: the server serves nothing that has not been finalized.
  const downloadable = attachment.status === 'ready'
  const kind = mediaBroken ? 'file' : attachment.preview_ready === true ? 'image' : mediaKind(attachment.mime_type)
  const wantsMedia = downloadable && kind !== 'file'

  const link = useDownloadLink(
    attachment.id,
    kind === 'image' && attachment.preview_ready === true,
    wantsMedia && inView,
  )
  const { reload } = link
  const full = useDownloadLink(attachment.id, mediaKind(attachment.mime_type) !== 'image', previewOpen && downloadable)
  useEffect(() => { setPreviewOpen(false) }, [attachment.id, attachment.status])

  const handleMediaError = useCallback((): void => {
    if (!retried.current) {
      retried.current = true
      reload()
      return
    }
    setMediaBroken(true)
  }, [reload])

  const href = downloadHref(attachment.id)
  const size = formatBytes(attachment.byte_size)
  // "PDF · 532 KB": what it is before how big it is, as on the card in the
  // sketches. Both come from the message, so neither costs a request.
  const fileKind = formatFileKind(attachment.filename, attachment.mime_type)

  let content: ReactElement

  if (attachment.status === 'blocked') {
    content = (
      <Notice
        tone="danger"
        icon={<ShieldIcon />}
        title="This file was blocked by virus scanning"
        detail={attachment.filename}
      />
    )
  } else if (attachment.status === 'deleted') {
    content = (
      <Notice
        tone="quiet"
        icon={<DocumentIcon mimeType={attachment.mime_type} />}
        title="This file was deleted"
        detail={attachment.filename}
      />
    )
  } else if (attachment.status === 'scanning' || attachment.status === 'pending') {
    content = (
      <div className={styles.notice} role="status">
        <Spinner size="sm" />
        <span className={styles.noticeBody}>
          <span className={styles.noticeTitle}>
            {attachment.status === 'scanning' ? 'Checking file…' : 'Waiting for upload…'}
          </span>
          <span className={styles.noticeDetail}>
            {attachment.filename}
            <span aria-hidden="true"> · </span>
            {size}
          </span>
        </span>
      </div>
    )
  } else if (kind === 'image') {
    content = (
      <figure className={styles.media}>
        {link.url === null ? (
          <div className={styles.placeholder} aria-hidden="true">
            {link.loading ? <Spinner size="sm" /> : null}
          </div>
        ) : (
          <button
            type="button"
            className={styles.mediaLink}
            onClick={() => { setPreviewFailed(false); setPreviewOpen(true) }}
            aria-label={`Open ${attachment.filename} at full size`}
          >
            <img
              className={styles.image}
              src={link.url}
              alt={attachment.filename}
              onError={handleMediaError}
            />
          </button>
        )}
        <figcaption className={styles.caption}>
          <span className={styles.captionName}>{attachment.filename}</span>
          <span className={styles.captionActions}>
            <span className={styles.captionMeta}>{size}</span>
            <a
              className={styles.mediaDownload}
              href={href}
              download={attachment.filename}
              aria-label={`Download ${attachment.filename}`}
              title={`Download ${attachment.filename}`}
            >
              <DownloadIcon />
            </a>
          </span>
        </figcaption>
        {link.error !== null ? <p className={styles.error}>{link.error}</p> : null}
      </figure>
    )
  } else if (kind === 'video') {
    content = (
      <figure className={styles.media}>
        {link.url === null ? (
          <div className={styles.placeholder} aria-hidden="true">
            {link.loading ? <Spinner size="sm" /> : null}
          </div>
        ) : (
          <video
            className={styles.video}
            src={link.url}
            controls
            preload="metadata"
            onError={handleMediaError}
          >
            {/* Read out when the element itself cannot play. */}
            <a href={href}>{attachment.filename}</a>
          </video>
        )}
        <figcaption className={styles.caption}>
          <span className={styles.captionName}>{attachment.filename}</span>
          <span className={styles.captionActions}>
            <span className={styles.captionMeta}>{size}</span>
            <a
              className={styles.mediaDownload}
              href={href}
              download={attachment.filename}
              aria-label={`Download ${attachment.filename}`}
              title={`Download ${attachment.filename}`}
            >
              <DownloadIcon />
            </a>
          </span>
        </figcaption>
        {link.error !== null ? <p className={styles.error}>{link.error}</p> : null}
      </figure>
    )
  } else {
    content = (
      <a className={styles.card} href={href} download={attachment.filename}>
        <span className={styles.cardIcon} aria-hidden="true">
          <DocumentIcon mimeType={attachment.mime_type} />
        </span>
        <span className={styles.cardBody}>
          <span className={styles.cardName}>{attachment.filename}</span>
          <span className={styles.cardMeta}>
            {fileKind}
            <span aria-hidden="true"> · </span>
            {size}
            {mediaBroken ? (
              <>
                <span aria-hidden="true"> · </span>
                Cannot be shown here
              </>
            ) : null}
          </span>
        </span>
        <span className={styles.cardAction} aria-hidden="true">
          <DownloadIcon />
        </span>
      </a>
    )
  }

  return (
    <div ref={containerRef} className={cx(styles.attachment, className)}>
      {content}
      <Modal
        open={previewOpen && downloadable}
        onClose={() => setPreviewOpen(false)}
        title={attachment.filename}
        size="lg"
        className={styles.previewDialog}
      >
        {full.loading ? <Spinner /> : null}
        {!previewFailed && full.url !== null ? (
          <img
            className={styles.previewImage}
            src={full.url}
            alt={attachment.filename}
            onError={() => setPreviewFailed(true)}
          />
        ) : null}
        {full.error !== null || previewFailed ? (
          <p role="alert">
            {full.error ?? 'This image could not be displayed.'}{' '}
            <button type="button" onClick={() => { setPreviewFailed(false); full.reload() }}>
              Retry image
            </button>
          </p>
        ) : null}
      </Modal>
    </div>
  )
}

export interface AttachmentListProps {
  attachments: readonly Attachment[]
  className?: string
}

/** The attachments of one message, in the order the server returned them. */
export function AttachmentList({ attachments, className }: AttachmentListProps): ReactElement | null {
  if (attachments.length === 0) return null

  return (
    <ul className={cx(styles.list, className)} aria-label="Attachments">
      {attachments.map((attachment) => (
        <li key={attachment.id} className={styles.listItem}>
          <AttachmentView attachment={attachment} />
        </li>
      ))}
    </ul>
  )
}
